/**
 * NotificationBell — bell icon button with unread count badge that opens
 * a dropdown notification feed. Uses Radix UI DropdownMenu for accessible
 * popover behavior (Escape to close, click outside, focus management).
 *
 * Requirements: 4.1, 4.2, 4.11
 */

import React, { useState } from 'react';
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import { useNotificationContext } from '../context/NotificationContext';
import { NotificationFeed } from './NotificationFeed';
import { cn } from '../lib/utils';

// ---------------------------------------------------------------------------
// Badge display helper
// ---------------------------------------------------------------------------

function formatBadgeCount(count: number): string {
  if (count > 99) return '99+';
  return String(count);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function NotificationBell(): React.ReactElement {
  const { unreadCount } = useNotificationContext();
  const [open, setOpen] = useState(false);

  return (
    <DropdownMenuPrimitive.Root open={open} onOpenChange={setOpen}>
      <DropdownMenuPrimitive.Trigger asChild>
        <button
          type="button"
          className={cn(
            'relative inline-flex items-center justify-center rounded-md p-2',
            'text-muted-foreground hover:text-foreground hover:bg-accent',
            'transition-colors focus-visible:outline-none focus-visible:ring-2',
            'focus-visible:ring-ring focus-visible:ring-offset-2',
          )}
          aria-label={
            unreadCount > 0
              ? `Notifications, ${unreadCount} unread`
              : 'Notifications'
          }
        >
          {/* Bell icon */}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="h-5 w-5"
            aria-hidden="true"
          >
            <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
            <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
          </svg>

          {/* Unread count badge */}
          {unreadCount > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 inline-flex items-center justify-center min-w-[1.125rem] h-[1.125rem] px-1 text-[0.625rem] font-bold text-white bg-destructive rounded-full leading-none"
              aria-live="polite"
              aria-label={`${unreadCount} unread notification${unreadCount !== 1 ? 's' : ''}`}
            >
              {formatBadgeCount(unreadCount)}
            </span>
          )}
        </button>
      </DropdownMenuPrimitive.Trigger>

      <DropdownMenuPrimitive.Portal>
        <DropdownMenuPrimitive.Content
          align="end"
          sideOffset={8}
          className="z-50 animate-in fade-in-0 zoom-in-95 data-[side=bottom]:slide-in-from-top-2"
          onCloseAutoFocus={(event) => {
            // Return focus to the bell trigger when closed
            event.preventDefault();
          }}
        >
          <NotificationFeed onClose={() => setOpen(false)} />
        </DropdownMenuPrimitive.Content>
      </DropdownMenuPrimitive.Portal>
    </DropdownMenuPrimitive.Root>
  );
}

// Export for testing
export { formatBadgeCount };
